import { EMPTY_ARR, EMPTY_SET } from '@dadohr/shared/domainTypes/emptyConstants';
import { createRoutes, Query } from '@dadohr/shared/domainTypes/routes';
import { DAY_FORMAT, isDayString } from '@dadohr/shared/domainTypes/time';
import { pick } from 'lodash';
import moment from 'moment-timezone';
import queryString from 'query-string';
import { useCallback, useMemo } from 'react';
import { useRouter } from './hooks/useRouter';
import { MomentRange } from './services/time';

let collectedQueryParams: Query = {};

const withoutEmpty = (query: Query) =>
  Object.keys(query).reduce((m, k) => {
    const v = query[k];
    if (v && v.length) {
      m[k] = v;
    }
    return m;
  }, {} as Query);

const withQuery = (url: string, params: Query) => {
  const queryParams = withoutEmpty(params);
  return `${url}${
    Object.keys(queryParams).length
      ? `?${queryString.stringify(queryParams)}`
      : ''
  }`;
};

export const useRoutes = () => {
  const { history, match } = useRouter();

  // Do not refactor the usage of history.location here - do not extract location to a variable,
  // or use location from useRouter. history.location is mutable - and this is exactly what we need in this
  // case to be able to make several calls to changeQuery in a row without losing the state of previous calls
  const getQuery = () => queryString.parse(history.location.search);
  collectedQueryParams = { ...collectedQueryParams, ...getQuery() };

  const getPath = () => history.location.pathname;
  const getParams = <T = any>() => match.params as T;
  const goTo = (url: string, replace = false) =>
    replace ? history.replace(url) : history.push(url);
  const getHash = () => history.location.hash;
  const updateHash = (nextHash: string) => {
    return goTo(`${withQuery(getPath(), getQuery())}#${nextHash}`);
  };

  const changeQuery = (queryParams: Query) => {
    // explicit changes need to update the collectedQueryParams, so that
    // params can be blanked. Otherwise they would stick around!
    collectedQueryParams = { ...collectedQueryParams, ...queryParams };
    return goTo(withQuery(getPath(), { ...getQuery(), ...queryParams }));
  };

  const toUrl = (
    path: string,
    query: Query = {},
    persistedQueryParams: string[] = []
  ) => ({
    path,
    query: {
      // If page A wants to pick param X, but moving to a page which
      // ignores param X, we want to set it again when we come back to
      // X. Hence we collect all query params over time, so that we
      // can always re-establish them for pages which are interested in them.
      ...pick(collectedQueryParams, [
        ...persistedQueryParams,
        'orgId',
        'new_login'
      ]),
      ...query
    }
  });

  const ROUTES = createRoutes(toUrl, queryString.stringify);

  return {
    getPath,
    getParams,
    getQuery,
    getHash,
    goTo,
    changeQuery,
    updateHash,
    ROUTES
  };
};

export const useQueryParam = <T>(
  param: string,
  fromParam: (p: string | null | undefined) => T,
  toParam: (v: T | null) => string | undefined // undefined removes the param
): [T, (nextValue: T | null) => void, (nextValue: T | null) => Query] => {
  const { getQuery, changeQuery } = useRoutes();
  let p = getQuery()[param];

  if (Array.isArray(p)) {
    console.log(`Param ${param} is array, expected a single value`);
    p = p[0];
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(() => fromParam(p as string | null | undefined), [p]);

  const setValue = useCallback(
    (v: T | null) =>
      changeQuery({
        [param]: toParam(v)
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [p, changeQuery]
  );

  return [value, setValue, (v) => ({ [param]: toParam(v) })];
};

export const useBooleanQueryParam = (param: string, defaultValue: boolean) => {
  return useQueryParam<boolean>(
    param,
    (t) => (t ? t === 'true' : defaultValue),
    (t) => (t === null ? undefined : `${t}`)
  );
};

export const useStringQueryParam = (param: string, defaultValue = '') =>
  useQueryParam<string>(
    param,
    (t) => t || defaultValue,
    (t) => t || undefined
  );

export const useTypedStringQueryParam = <T extends string>(
  param: string,
  defaultValue: T,
  blankOutDefaultValue?: boolean
) =>
  useQueryParam<T>(
    param,
    (t) => (t as T) || defaultValue,
    (t) =>
      blankOutDefaultValue && t === defaultValue ? undefined : t || undefined
  );

export const useNullableTypedStringQueryParam = <T extends string>(
  param: string,
  defaultValue: T | null = null,
  blankOutDefaultValue?: boolean
) =>
  useQueryParam<T | null>(
    param,
    (t) => (t as T) || defaultValue,
    (t) =>
      blankOutDefaultValue && t === defaultValue ? undefined : t || undefined
  );

export const useNumberQueryParam = (param: string, defaultValue = 0) =>
  useQueryParam<number>(
    param,
    (t) => (t ? parseInt(t, 10) : defaultValue),
    (t) => `${t}`
  );

export const useComboQueryParam = <T>(
  param: string,
  fromParam: (p: [string | undefined, string | undefined]) => T,
  toParam: (v: T | null) => [string | undefined, string | undefined],
  delimiter = '---'
) =>
  useQueryParam(
    param,
    (p) => {
      const [a, b] = (p || '').split(delimiter);
      return fromParam([a, b]);
    },
    (v) => toParam(v).join(delimiter)
  );

const LIST_SEPARATOR = ',';
export const listToQueryParam = (xs: string[]): string =>
  [...xs].sort().join(LIST_SEPARATOR);

export const setToQueryParam = (xs: Set<string>): string =>
  [...xs].sort().join(LIST_SEPARATOR);
export const queryParamToList = <T extends string>(x: string): T[] =>
  x.split(LIST_SEPARATOR) as T[];

export const queryParamToSet = <T extends string>(x: string): Set<T> =>
  new Set(queryParamToList(x));

export const useNullableStringSetQueryParam = (param: string) => {
  return useQueryParam(
    param,
    (p) => (p ? queryParamToSet(p) : null),
    (v) => (v ? setToQueryParam(v) : undefined)
  );
};

export const useDayRangeQueryParam = (
  param: string

  // fromParam: (p: [string | undefined, string | undefined]) => MomentRange,
  // toParam: (v: MomentRange | null) => [string | undefined, string | undefined],
) => {
  const delimiter = '---';
  const f = DAY_FORMAT;

  return useQueryParam<MomentRange | null>(
    param,
    (p) => {
      if (!p) {
        return null;
      }
      const [start = '', end = ''] = p.split(delimiter);
      if (isDayString(start) && isDayString(end)) {
        return {
          start: moment(start, f).startOf('d'),
          end: moment(end, f).startOf('d')
        };
      }
      return null;
    },
    (r) => {
      if (!r) {
        return undefined;
      }
      return `${r.start.format(DAY_FORMAT)}${delimiter}${r.end.format(
        DAY_FORMAT
      )}`;
    }
  );
};

export const useStringListQueryParam = (
  param: string,
  defaultValue: string[] = EMPTY_ARR
) =>
  useQueryParam<string[]>(
    param,
    (t) => (t ? queryParamToList(t) : defaultValue),
    (t) => (t ? listToQueryParam(t) : undefined)
  );

export const useStringSetQueryParam = (
  param: string,
  defaultValue: Set<string> = EMPTY_SET as Set<string>
) =>
  useQueryParam<Set<string>>(
    param,
    (t) => (t ? queryParamToSet(t) : defaultValue),
    (t) => (t ? setToQueryParam(t) : undefined)
  );
